import { Box, Stack, Theme } from "@mui/material"
import { useMediaQuery } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { AnimatePresence } from "framer-motion"
import { useCallback, useEffect, useMemo, useState } from "react"

import { useCachedAo } from "../ao/ao"
import { getPoolsOverview } from "../ao/monitor"
import Button from "../components/Button"
import { InMemoryTable } from "../components/InMemoryTable"
import { PageWrapper } from "../components/PageWrapper"
import SearchPool from "../components/SearchPool"
import Skeleton from "../components/Skeleton"
import PoolsTableRow from "../components/pools/PoolsTableRow"
import ProtocolVolumeChart from "../components/pools/ProtocolVolumeChart"
import TopActions from "../components/pools/TopActions"
import usePoolPnlHistory from "../hooks/use-pool-pnl-history"
import { createLogger } from "../utils/logger"

const logger = createLogger("pools")

export default function Pools() {
  useEffect(() => {
    document.title = "Liquidity Pools · Botega"
  }, [])

  const ao = useCachedAo()
  const [search, setSearch] = useState("")
  const [pinnedPoolIds, setPinnedPoolIds] = useState<Set<string>>(new Set())

  const { data: pools, isLoading } = useQuery({
    queryKey: ["pools-page"],
    queryFn: async () => {
      const records = await getPoolsOverview(ao)
      logger.log("onPoolsOverview", records)
      return records
    },
  })

  const { data: userPnl } = usePoolPnlHistory()

  useEffect(() => {
    if (userPnl) {
      userPnl.pools.forEach((pool) => {
        if (pool.user_share > 0) {
          setPinnedPoolIds((prevPinnedPoolIds) => new Set(prevPinnedPoolIds).add(pool.amm_process))
        }
      })
    }
  }, [userPnl])

  const handlePoolShare = useCallback(
    (poolId: string, userShares: number) => {
      if (userShares > 0 && !pinnedPoolIds.has(poolId)) {
        logger.log("Sending pool to top", poolId)
        setPinnedPoolIds((prevPinnedPoolIds) => new Set(prevPinnedPoolIds).add(poolId))
      }
    },
    [pinnedPoolIds],
  )

  const filteredAndSortedPools = useMemo(() => {
    if (!pools) return []

    return pools
      .filter((pool) => {
        if (!search) return true
        const searchLower = search.toLowerCase()
        return (
          pool.amm_process?.toLowerCase().includes(searchLower) ||
          pool.base_token_name?.toLowerCase().includes(searchLower) ||
          pool.base_token_ticker?.toLowerCase().includes(searchLower) ||
          pool.quote_token_name?.toLowerCase().includes(searchLower) ||
          pool.quote_token_ticker?.toLowerCase().includes(searchLower)
        )
      })
      .sort((a, b) => {
        const aIsPinned = pinnedPoolIds.has(a.amm_process)
        const bIsPinned = pinnedPoolIds.has(b.amm_process)
        if (aIsPinned && !bIsPinned) return -1
        if (!aIsPinned && bIsPinned) return 1
        return 0
      })
  }, [pools, search, pinnedPoolIds])

  const [pageSize, setPageSize] = useState(30)

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  if (isLoading || !pools) {
    return (
      <PageWrapper>
        <Stack gap={1}>
          <Skeleton height="28px" />
          <Skeleton height="28px" />
          <Skeleton height="28px" />
          <Skeleton height="28px" />
        </Stack>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <Stack direction="column" spacing={4}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
            <TopActions />
          </Stack>
          <ProtocolVolumeChart />
        </Stack>
        <Stack direction="row" justifyContent="center">
          <Box width={500}>
            <SearchPool handleChange={(e) => setSearch(e.target.value)} />
          </Box>
        </Stack>
        <AnimatePresence initial={false}>
          <InMemoryTable
            loading={isLoading}
            initialSortField="balance"
            initialSortDir="desc"
            data={filteredAndSortedPools}
            pageSize={pageSize}
            headerCells={
              isMobile
                ? []
                : [
                    { label: "#" },
                    { label: "Pool" },
                    { label: "Transactions", align: "right" },
                    { label: "Total Pool Share", align: "right" },
                    { label: "30 day APY" },
                    { label: "Liquidity Actions", align: "right" },
                  ]
            }
            renderRow={(pool) => (
              <PoolsTableRow key={pool.tvl_rank} pool={pool} handlePoolShare={handlePoolShare} />
            )}
          />
        </AnimatePresence>
        <Button
          style={{ alignSelf: "center" }}
          className="secondary"
          onClick={() => {
            setPageSize((prev) => prev + 30)
          }}
        >
          Load more records
        </Button>
      </Stack>
    </PageWrapper>
  )
}
